import { useState, useMemo, useEffect, useCallback } from 'react';
import useAppStore from '../../store/app.store';
import { IcImage, IcVideo, IcSmartphone, IcLayers, IcFile, IcEye, IcCheckCircle, IcXCircle, IcX, IcCopy, IcLink, IcCalendar, IcFilm } from '../../components/ui/Icons';
import PageHeader from '../../components/ui/PageHeader';
import Modal from '../../components/ui/Modal';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';

const FLUXO = [
  { id: 'copy',                 label: 'Copy',           cor: 'bg-gray-100',    texto: 'text-gray-700',   borda: 'border-gray-300'   },
  { id: 'roteiro',              label: 'Roteiro',        cor: 'bg-blue-50',     texto: 'text-blue-700',   borda: 'border-blue-300'   },
  { id: 'criativo',             label: 'Criativo',       cor: 'bg-purple-50',   texto: 'text-purple-700', borda: 'border-purple-300' },
  { id: 'captacao',             label: 'Captação',       cor: 'bg-yellow-50',   texto: 'text-yellow-700', borda: 'border-yellow-300' },
  { id: 'apresentacao',         label: 'Apresentação',   cor: 'bg-orange-50',   texto: 'text-orange-700', borda: 'border-orange-300' },
  { id: 'aguardando_aprovacao', label: 'Ag. Aprovação',  cor: 'bg-pink-50',     texto: 'text-pink-700',   borda: 'border-pink-300'   },
  { id: 'em_ajuste',            label: 'Em Ajuste',      cor: 'bg-red-50',      texto: 'text-red-700',    borda: 'border-red-300'    },
  { id: 'agendado',             label: 'Agendado',       cor: 'bg-green-50',    texto: 'text-green-700',  borda: 'border-green-300'  },
  { id: 'publicado',            label: 'Publicado',      cor: 'bg-emerald-100', texto: 'text-emerald-700',borda: 'border-emerald-300'},
];

const FLUXO_ORDEM = FLUXO.map(f => f.id);

const TIPO_ICON = {
  post_feed: <IcImage size={15} />,
  reel:      <IcVideo size={15} />,
  story:     <IcSmartphone size={15} />,
  carrossel: <IcLayers size={15} />,
  outro:     <IcFile size={15} />,
};

const ETIQUETA_CORES = ['bg-blue-100 text-blue-700','bg-purple-100 text-purple-700','bg-green-100 text-green-700','bg-yellow-100 text-yellow-700','bg-red-100 text-red-700','bg-orange-100 text-orange-700'];
const TABS = ['Produção', 'Planejamento', 'Captações', 'Demandas Extras'];

export default function Midias() {
  const [tab, setTab] = useState(0);
  return (
    <div>
      <PageHeader title="Mídias Sociais" subtitle="Gestão de conteúdo e produção" />
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === i ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
            {t}
          </button>
        ))}
      </div>
      {tab === 0 && <TabProducao />}
      {tab === 1 && <TabPlanejamento />}
      {tab === 2 && <TabCaptacoes />}
      {tab === 3 && <TabDemandasExtras />}
    </div>
  );
}

// ─── ABA PRODUÇÃO ────────────────────────────────────────────────────────────
function TabProducao() {
  const usuario = useAppStore(s => s.usuario);
  const isAdmin = usuario?.nivel_acesso <= 1;
  const [producoes, setProducoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [filtroCliente, setFiltroCliente] = useState('');
  const [modalNovo, setModalNovo] = useState(false);
  const [editando, setEditando] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const params = filtroCliente ? { cliente_id: filtroCliente } : {};
      const { data } = await api.get('/midias/producoes', { params });
      setProducoes(data || []);
    } catch { toast.error('Erro ao carregar produções.'); }
  }, [filtroCliente]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/clientes/lista').then(r => setClientes(r.data || [])).catch(() => {});
    api.get('/usuarios/equipe').then(r => setUsuarios(r.data || [])).catch(() => {});
  }, []);

  const porColuna = useMemo(() => {
    const m = {};
    FLUXO.forEach(f => { m[f.id] = producoes.filter(p => p.status === f.id); });
    return m;
  }, [producoes]);

  const handleDrop = async (novoStatus) => {
    if (!dragging || !novoStatus) return;
    const p = producoes.find(x => x.id === dragging);
    if (!p || p.status === novoStatus) { setDragging(null); setDragOverCol(null); return; }
    setProducoes(prev => prev.map(x => x.id === dragging ? { ...x, status: novoStatus } : x));
    setDragging(null); setDragOverCol(null);
    try {
      await api.patch(`/midias/producoes/${dragging}`, { status: novoStatus });
    } catch { toast.error('Erro ao mover.'); carregar(); }
  };

  const avancar = async (p) => {
    const idx = FLUXO_ORDEM.indexOf(p.status);
    if (idx < FLUXO_ORDEM.length - 1) {
      const novoStatus = FLUXO_ORDEM[idx + 1];
      setProducoes(prev => prev.map(x => x.id === p.id ? { ...x, status: novoStatus } : x));
      try { await api.patch(`/midias/producoes/${p.id}`, { status: novoStatus }); toast.success('Avançado!'); }
      catch { carregar(); }
    }
  };

  const salvar = async (dados) => {
    try {
      if (editando?.id) {
        await api.patch(`/midias/producoes/${editando.id}`, dados);
        toast.success('Salvo!');
      } else {
        await api.post('/midias/producoes', dados);
        toast.success('Produção criada!');
      }
      setModalNovo(false); setEditando(null); carregar();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar.'); }
  };

  const excluir = async (id) => {
    try { await api.delete(`/midias/producoes/${id}`); toast.success('Excluído!'); setEditando(null); carregar(); }
    catch { toast.error('Erro ao excluir.'); }
  };

  const atualizar = async (id, dados) => {
    try {
      const { data } = await api.patch(`/midias/producoes/${id}`, dados);
      setProducoes(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
      setDetalhe(prev => prev ? { ...prev, ...data } : prev);
    } catch { toast.error('Erro ao atualizar.'); }
  };

  return (
    <div>
      <div className="flex gap-3 mb-5 items-center flex-wrap">
        {isAdmin && (
          <select className="form-input w-auto" value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
            <option value="">Todos os clientes</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
        )}
        <button onClick={() => setModalNovo(true)} className="btn-primary ml-auto">+ Nova Produção</button>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4">
        {FLUXO.map(col => (
          <div key={col.id}
            className={`flex-shrink-0 w-[82vw] sm:w-64 min-w-[260px] rounded-xl p-3 flex flex-col gap-2 ${col.cor} ${dragOverCol === col.id ? 'ring-2 ring-brand-400' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragOverCol(col.id); }}
            onDragLeave={() => setDragOverCol(null)}
            onDrop={e => { e.preventDefault(); handleDrop(col.id); }}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs font-bold uppercase tracking-wider ${col.texto}`}>{col.label}</span>
              <span className="text-xs bg-white rounded-full px-2 py-0.5 shadow-sm text-gray-500">{porColuna[col.id]?.length || 0}</span>
            </div>
            {(porColuna[col.id] || []).map(p => (
              <KanbanCard key={p.id} producao={p} col={col}
                onDragStart={() => setDragging(p.id)}
                onAvancar={() => avancar(p)}
                onAprovar={() => atualizar(p.id, { status: 'agendado' })}
                onAjuste={() => atualizar(p.id, { status: 'em_ajuste' })}
                onDetalhe={() => setDetalhe(p)}
                onEditar={() => setEditando(p)} />
            ))}
            {!porColuna[col.id]?.length && (
              <div className="border-2 border-dashed border-white/60 rounded-lg p-3 text-center text-xs text-gray-400 min-h-[50px] flex items-center justify-center">Solte aqui</div>
            )}
          </div>
        ))}
      </div>

      <Modal open={modalNovo} onClose={() => setModalNovo(false)} title="Nova Produção" size="xl">
        <FormProducao clientes={clientes} usuarios={usuarios}
          onSalvar={salvar} onFechar={() => setModalNovo(false)} />
      </Modal>
      <Modal open={!!editando} onClose={() => setEditando(null)} title="Editar Produção" size="xl">
        {editando && <FormProducao inicial={editando} clientes={clientes} usuarios={usuarios}
          onSalvar={salvar} onFechar={() => setEditando(null)}
          onExcluir={() => { if (window.confirm(`Excluir "${editando.titulo}"?`)) excluir(editando.id); }} />}
      </Modal>
      <Modal open={!!detalhe} onClose={() => setDetalhe(null)} title={detalhe?.titulo} size="xl">
        {detalhe && <DetalheProducao producao={detalhe} usuarios={usuarios}
          onUpdate={(dados) => atualizar(detalhe.id, dados)}
          onEditar={() => { setEditando(detalhe); setDetalhe(null); }} />}
      </Modal>
    </div>
  );
}

function KanbanCard({ producao: p, col, onDragStart, onAvancar, onAprovar, onAjuste, onDetalhe, onEditar }) {
  const checkFeitos = (p.checklist || []).filter(c => c.feito).length;
  const checkTotal = (p.checklist || []).length;
  const clienteNome = p.cliente_fantasia || p.cliente_nome || '';

  return (
    <div draggable onDragStart={onDragStart}
      className="bg-white rounded-lg p-3 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow group">
      <div className="flex items-start gap-2 mb-1.5">
        <span className="flex-shrink-0 text-gray-500">{TIPO_ICON[p.tipo] || <IcFile size={15} />}</span>
        <p className="text-xs font-semibold text-gray-800 leading-tight flex-1">{p.titulo}</p>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button onClick={e => { e.stopPropagation(); onEditar(); }} className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-brand-600 hover:bg-brand-50">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
          <button onClick={e => { e.stopPropagation(); onDetalhe(); }} className="w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <IcEye size={11} />
          </button>
        </div>
      </div>
      {clienteNome && <p className="text-xs text-brand-600 font-medium mb-1.5">{clienteNome}</p>}
      {(p.etiquetas || []).length > 0 && (
        <div className="flex gap-1 flex-wrap mb-1.5">
          {p.etiquetas.map((e, i) => <span key={e} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${ETIQUETA_CORES[i % ETIQUETA_CORES.length]}`}>{e}</span>)}
        </div>
      )}
      {checkTotal > 0 && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-brand-500 rounded-full" style={{ width: `${(checkFeitos / checkTotal) * 100}%` }} />
          </div>
          <span className="text-[10px] text-gray-400">{checkFeitos}/{checkTotal}</span>
        </div>
      )}
      {p.link_drive && (
        <a href={p.link_drive} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
          className="flex items-center gap-1 text-[10px] text-blue-500 hover:underline mb-1.5">📁 Drive</a>
      )}
      {p.prazo && (
        <p className={`text-[10px] mb-1.5 ${new Date(p.prazo) < new Date() && p.status !== 'publicado' ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
          <span className="flex items-center gap-0.5"><IcCalendar size={10} /> {format(new Date(p.prazo), 'dd/MM', { locale: ptBR })}</span>
        </p>
      )}
      {p.status !== 'publicado' && p.status !== 'agendado' && p.status !== 'aguardando_aprovacao' && (
        <button onClick={e => { e.stopPropagation(); onAvancar(); }}
          className="mt-1 w-full text-xs py-1 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-100 font-medium transition-colors">
          Avançar →
        </button>
      )}
      {p.status === 'aguardando_aprovacao' && (
        <div className="flex gap-1 mt-1">
          <button onClick={e => { e.stopPropagation(); onAprovar(); }} className="flex-1 text-xs py-1 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 font-medium flex items-center justify-center gap-1"><IcCheckCircle size={11} /> Aprovado</button>
          <button onClick={e => { e.stopPropagation(); onAjuste(); }} className="flex-1 text-xs py-1 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 font-medium flex items-center justify-center gap-1"><IcXCircle size={11} /> Ajuste</button>
        </div>
      )}
    </div>
  );
}

function FormProducao({ inicial, clientes, usuarios, onSalvar, onFechar, onExcluir }) {
  const [form, setForm] = useState({
    cliente_id: '', titulo: '', tipo: 'post_feed', plataforma: 'instagram',
    responsavel_id: '', prazo: '', legenda: '', copy_texto: '', roteiro: '',
    checklist: [], etiquetas: [], link_drive: '', observacoes: '',
    ...(inicial || {}),
    prazo: inicial?.prazo ? inicial.prazo.slice(0, 10) : '',
    checklist: inicial?.checklist || [],
    etiquetas: inicial?.etiquetas || [],
  });
  const [novaEtiqueta, setNovaEtiqueta] = useState('');
  const [novoItem, setNovoItem] = useState('');
  const [abaForm, setAbaForm] = useState(0);
  const ABAS_FORM = ['Geral', 'Copy & Roteiro', 'Checklist', 'Extras'];
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const adicionarEtiqueta = () => {
    const t = novaEtiqueta.trim();
    if (t && !form.etiquetas.includes(t)) { set('etiquetas', [...form.etiquetas, t]); setNovaEtiqueta(''); }
  };
  const adicionarItem = () => {
    const t = novoItem.trim();
    if (t) { set('checklist', [...form.checklist, { texto: t, feito: false }]); setNovoItem(''); }
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(form); }} className="space-y-4">
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {ABAS_FORM.map((a, i) => (
          <button type="button" key={a} onClick={() => setAbaForm(i)}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-all ${abaForm === i ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>{a}</button>
        ))}
      </div>

      {abaForm === 0 && (
        <div className="space-y-3">
          <div><label className="form-label">Cliente *</label>
            <select className="form-input" value={form.cliente_id} onChange={e => set('cliente_id', e.target.value)} required>
              <option value="">Selecione...</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
            </select>
          </div>
          <div><label className="form-label">Título *</label>
            <input className="form-input" value={form.titulo} onChange={e => set('titulo', e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="form-label">Tipo</label>
              <select className="form-input" value={form.tipo} onChange={e => set('tipo', e.target.value)}>
                <option value="post_feed">Post Feed</option><option value="reel">Reel</option>
                <option value="story">Story</option><option value="carrossel">Carrossel</option><option value="outro">Outro</option>
              </select>
            </div>
            <div><label className="form-label">Plataforma</label>
              <select className="form-input" value={form.plataforma} onChange={e => set('plataforma', e.target.value)}>
                <option value="instagram">Instagram</option><option value="facebook">Facebook</option>
                <option value="tiktok">TikTok</option><option value="linkedin">LinkedIn</option>
                <option value="youtube">YouTube</option><option value="midia_off">Mídia Off</option>
              </select>
            </div>
            <div><label className="form-label">Responsável</label>
              <select className="form-input" value={form.responsavel_id} onChange={e => set('responsavel_id', e.target.value)}>
                <option value="">Selecione...</option>
                {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </div>
            <div><label className="form-label">Prazo</label>
              <input type="date" className="form-input" value={form.prazo} onChange={e => set('prazo', e.target.value)} />
            </div>
          </div>
          <div><label className="form-label">Observações</label>
            <textarea className="form-input" rows={3} value={form.observacoes || ''} onChange={e => set('observacoes', e.target.value)} />
          </div>
        </div>
      )}

      {abaForm === 1 && (
        <div className="space-y-3">
          <div><label className="form-label">Legenda / Copy da publicação</label>
            <textarea className="form-input" rows={4} value={form.legenda} onChange={e => set('legenda', e.target.value)} /></div>
          <div><label className="form-label">Copy interno (briefing)</label>
            <textarea className="form-input" rows={4} value={form.copy_texto} onChange={e => set('copy_texto', e.target.value)} /></div>
          <div><label className="form-label">Roteiro (para Reels / Vídeos)</label>
            <textarea className="form-input font-mono text-xs" rows={6} value={form.roteiro} onChange={e => set('roteiro', e.target.value)} /></div>
        </div>
      )}

      {abaForm === 2 && (
        <div className="space-y-3">
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {form.checklist.map((item, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <input type="checkbox" checked={item.feito} onChange={() => set('checklist', form.checklist.map((c, idx) => idx === i ? { ...c, feito: !c.feito } : c))} />
                <span className={`text-sm flex-1 ${item.feito ? 'line-through text-gray-400' : 'text-gray-700'}`}>{item.texto}</span>
                <button type="button" onClick={() => set('checklist', form.checklist.filter((_, idx) => idx !== i))} className="text-red-400"><IcX size={12} /></button>
              </div>
            ))}
            {!form.checklist.length && <p className="text-xs text-gray-400 text-center py-4">Nenhum item ainda.</p>}
          </div>
          <div className="flex gap-2">
            <input className="form-input flex-1" value={novoItem} onChange={e => setNovoItem(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); adicionarItem(); } }} placeholder="Novo item..." />
            <button type="button" onClick={adicionarItem} className="btn-secondary px-3">+ Add</button>
          </div>
        </div>
      )}

      {abaForm === 3 && (
        <div className="space-y-4">
          <div>
            <label className="form-label">Etiquetas</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.etiquetas.map(e => (
                <span key={e} className="flex items-center gap-1 text-xs bg-brand-50 text-brand-700 px-2 py-1 rounded-full">
                  {e} <button type="button" onClick={() => set('etiquetas', form.etiquetas.filter(x => x !== e))} className="text-brand-400 hover:text-red-500">✕</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input className="form-input flex-1" value={novaEtiqueta} onChange={e => setNovaEtiqueta(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); adicionarEtiqueta(); } }} placeholder="Nova etiqueta..." />
              <button type="button" onClick={adicionarEtiqueta} className="btn-secondary px-3">+ Add</button>
            </div>
          </div>
          <div><label className="form-label">📁 Link do Google Drive</label>
            <input className="form-input" type="url" value={form.link_drive} onChange={e => set('link_drive', e.target.value)} placeholder="https://drive.google.com/..." />
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button type="submit" className="btn-primary flex-1">{inicial ? 'Salvar alterações' : 'Criar produção'}</button>
        {inicial && onExcluir && (
          <button type="button" onClick={onExcluir} className="btn-secondary text-red-500 border-red-200">Excluir</button>
        )}
        <button type="button" onClick={onFechar} className="btn-secondary">Fechar</button>
      </div>
    </form>
  );
}

function DetalheProducao({ producao: p, usuarios, onUpdate, onEditar }) {
  const [feedback, setFeedback] = useState(p.feedback_cliente || '');
  const [dataPublicacao, setDataPublicacao] = useState(p.data_publicacao || '');
  const etapaAtual = FLUXO.find(f => f.id === p.status);
  const clienteNome = p.cliente_fantasia || p.cliente_nome || '';
  const responsavelNome = usuarios.find(u => u.id === p.responsavel_id)?.nome || '';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-3xl">{TIPO_ICON[p.tipo]}</span>
        <div className="flex-1">
          {clienteNome && <p className="text-sm font-bold text-gray-800">{clienteNome}</p>}
          <p className="text-xs text-gray-500 capitalize">{p.plataforma} · {p.tipo?.replace('_', ' ')}</p>
        </div>
        <span className={`text-xs font-bold px-3 py-1 rounded-full ${etapaAtual?.cor} ${etapaAtual?.texto}`}>{etapaAtual?.label}</span>
        <button onClick={onEditar} className="btn-secondary text-xs px-3 py-1.5">Editar</button>
      </div>
      {(p.etiquetas || []).length > 0 && (
        <div className="flex gap-1 flex-wrap">
          {p.etiquetas.map((e, i) => <span key={e} className={`text-xs px-2 py-0.5 rounded-full font-medium ${ETIQUETA_CORES[i % ETIQUETA_CORES.length]}`}>{e}</span>)}
        </div>
      )}
      {p.link_drive && (
        <a href={p.link_drive} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 p-3 bg-blue-50 rounded-xl text-sm text-blue-700 hover:bg-blue-100 font-medium">
          📁 Abrir pasta no Google Drive
        </a>
      )}
      {(p.checklist || []).length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2">Checklist ({p.checklist.filter(c => c.feito).length}/{p.checklist.length})</p>
          <div className="space-y-1.5">
            {p.checklist.map((item, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded-lg">
                <input type="checkbox" checked={item.feito} onChange={() => {
                  const novo = p.checklist.map((c, idx) => idx === i ? { ...c, feito: !c.feito } : c);
                  onUpdate({ checklist: novo });
                }} />
                <span className={`text-sm ${item.feito ? 'line-through text-gray-400' : 'text-gray-700'}`}>{item.texto}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {p.legenda && <div className="p-3 bg-gray-50 rounded-xl"><p className="text-xs font-semibold text-gray-500 mb-1">Legenda</p><p className="text-sm text-gray-700 whitespace-pre-wrap">{p.legenda}</p></div>}
      {p.copy_texto && <div className="p-3 bg-purple-50 rounded-xl"><p className="text-xs font-semibold text-purple-600 mb-1">Copy interno</p><p className="text-sm text-gray-700 whitespace-pre-wrap">{p.copy_texto}</p></div>}
      {p.roteiro && <div className="p-3 bg-blue-50 rounded-xl"><p className="text-xs font-semibold text-blue-600 mb-1">Roteiro</p><p className="text-sm text-gray-700 whitespace-pre-wrap font-mono">{p.roteiro}</p></div>}
      {p.status === 'em_ajuste' && (
        <div className="space-y-2 p-3 bg-red-50 rounded-xl border border-red-100">
          <label className="form-label text-red-600">Feedback do cliente</label>
          <textarea className="form-input border-red-200" rows={3} value={feedback} onChange={e => setFeedback(e.target.value)} />
          <button onClick={() => onUpdate({ feedback_cliente: feedback })} className="btn-primary w-full">Salvar feedback</button>
        </div>
      )}
      {p.status === 'agendado' && (
        <div className="space-y-2 p-3 bg-green-50 rounded-xl border border-green-100">
          <label className="form-label text-green-700">Data de publicação</label>
          <input type="datetime-local" className="form-input" value={dataPublicacao} onChange={e => setDataPublicacao(e.target.value)} />
          <button onClick={() => onUpdate({ data_publicacao: dataPublicacao })} className="btn-primary w-full">Confirmar agendamento</button>
        </div>
      )}
      <div className="text-xs text-gray-400 space-y-0.5 pt-2 border-t border-gray-100">
        {p.prazo && <p>Prazo: <strong className="text-gray-600">{format(new Date(p.prazo), "dd 'de' MMMM", { locale: ptBR })}</strong></p>}
        {responsavelNome && <p>Responsável: <strong className="text-gray-600">{responsavelNome}</strong></p>}
        <p>Criado em: {format(new Date(p.criado_em), "dd/MM/yyyy 'às' HH:mm")}</p>
      </div>
    </div>
  );
}

// ─── ABA PLANEJAMENTO ────────────────────────────────────────────────────────
function TabPlanejamento() {
  const [semestrais, setSemestrais] = useState([]);
  const [mensais, setMensais] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [subTab, setSubTab] = useState(0);
  const [filtroCliente, setFiltroCliente] = useState('');
  const [modalSemestral, setModalSemestral] = useState(false);
  const [modalMensal, setModalMensal] = useState(false);
  const [editandoSemestral, setEditandoSemestral] = useState(null);

  useEffect(() => {
    api.get('/midias/planejamentos/semestrais').then(r => setSemestrais(r.data || [])).catch(() => {});
    api.get('/midias/planejamentos/mensais').then(r => setMensais(r.data || [])).catch(() => {});
    api.get('/clientes/lista').then(r => setClientes(r.data || [])).catch(() => {});
  }, []);

  const salvarSemestral = async (dados) => {
    try {
      if (editandoSemestral?.id) {
        const { data } = await api.patch(`/midias/planejamentos/semestrais/${editandoSemestral.id}`, dados);
        setSemestrais(prev => prev.map(p => p.id === editandoSemestral.id ? data : p));
        toast.success('Planejamento atualizado!');
      } else {
        const { data } = await api.post('/midias/planejamentos/semestrais', dados);
        setSemestrais(prev => [...prev, data]);
        toast.success('Planejamento semestral criado!');
      }
      setModalSemestral(false); setEditandoSemestral(null);
    } catch (err) { toast.error(err.response?.data?.error || 'Erro.'); }
  };

  const excluirSemestral = async (id) => {
    if (!window.confirm('Excluir este planejamento?')) return;
    try { await api.delete(`/midias/planejamentos/semestrais/${id}`); setSemestrais(prev => prev.filter(p => p.id !== id)); toast.success('Excluído.'); }
    catch { toast.error('Erro ao excluir.'); }
  };

  const salvarMensal = async (dados) => {
    try {
      const { data } = await api.post('/midias/planejamentos/mensais', dados);
      setMensais(prev => [...prev, data]); toast.success('Planejamento mensal criado!'); setModalMensal(false);
    } catch (err) { toast.error(err.response?.data?.error || 'Erro.'); }
  };

  const excluirMensal = async (id) => {
    if (!window.confirm('Excluir este planejamento?')) return;
    try { await api.delete(`/midias/planejamentos/mensais/${id}`); setMensais(prev => prev.filter(p => p.id !== id)); toast.success('Excluído.'); }
    catch { toast.error('Erro ao excluir.'); }
  };

  const semFiltered = semestrais.filter(p => !filtroCliente || p.cliente_id === filtroCliente);
  const menFiltered = mensais.filter(p => !filtroCliente || p.cliente_id === filtroCliente);
  const meses = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

  return (
    <div>
      <div className="flex items-center gap-4 mb-5 flex-wrap">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {['Semestral','Mensal'].map((t, i) => (
            <button key={t} onClick={() => setSubTab(i)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${subTab === i ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>{t}</button>
          ))}
        </div>
        <select className="form-input w-auto" value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
          <option value="">Todos os clientes</option>
          {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
        </select>
        <button onClick={() => subTab === 0 ? setModalSemestral(true) : setModalMensal(true)} className="btn-primary ml-auto">+ Novo Planejamento</button>
      </div>

      {subTab === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {semFiltered.map(p => (
            <div key={p.id} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-gray-900">{p.cliente_nome}</p>
                  <p className="text-xs text-brand-600 font-medium">{p.semestre}º Semestre {p.ano}</p>
                </div>
                <span className="text-xs bg-brand-50 text-brand-700 px-2 py-1 rounded-full font-semibold">{p.quantidade_posts_semana}x/semana</span>
              </div>
              <p className="text-xs text-gray-600 mb-3"><strong>Objetivo:</strong> {p.objetivos}</p>
              <div className="flex flex-wrap gap-1 mb-2">{(p.temas || []).map(t => <span key={t} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{t}</span>)}</div>
              <div className="flex gap-1 flex-wrap mb-3">{(p.plataformas || []).map(pl => <span key={pl} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full capitalize">{pl}</span>)}</div>
              <div className="flex gap-3 border-t border-gray-100 pt-3">
                <button onClick={() => { setEditandoSemestral(p); setModalSemestral(true); }} className="text-xs text-brand-600 hover:underline font-medium">Editar</button>
                <button onClick={() => excluirSemestral(p.id)} className="text-xs text-red-500 hover:underline">Excluir</button>
              </div>
            </div>
          ))}
          {!semFiltered.length && <div className="col-span-2 text-center py-12 text-gray-400">Nenhum planejamento semestral cadastrado.</div>}
        </div>
      )}

      {subTab === 1 && (
        <div className="space-y-4">
          {menFiltered.map(p => (
            <div key={p.id} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-semibold text-gray-900">{p.cliente_nome}</p>
                  <p className="text-xs text-brand-600 font-medium">{meses[p.mes - 1]} {p.ano}</p>
                </div>
              </div>
              {(p.grade || []).map(semana => (
                <div key={semana.semana} className="mb-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Semana {semana.semana}</p>
                  <div className="flex gap-2 flex-wrap">
                    {(semana.dias || []).map((d, i) => (
                      <div key={i} className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 min-w-[120px]">
                        <p className="font-semibold text-gray-700">{d.dia}</p>
                        <p className="text-gray-500">{d.tipo}</p>
                        <p className="text-gray-400 truncate">{d.tema}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {p.observacoes && <p className="text-xs text-gray-500 mt-2 italic">{p.observacoes}</p>}
              <div className="flex gap-3 border-t border-gray-100 pt-3 mt-3">
                <button onClick={() => excluirMensal(p.id)} className="text-xs text-red-500 hover:underline">Excluir</button>
              </div>
            </div>
          ))}
          {!menFiltered.length && <div className="text-center py-12 text-gray-400">Nenhum planejamento mensal cadastrado.</div>}
        </div>
      )}

      <Modal open={modalSemestral} onClose={() => { setModalSemestral(false); setEditandoSemestral(null); }} title={editandoSemestral ? 'Editar Planejamento' : 'Novo Planejamento Semestral'}>
        <FormPlanejamentoSemestral inicial={editandoSemestral} clientes={clientes} onSalvar={salvarSemestral} onFechar={() => { setModalSemestral(false); setEditandoSemestral(null); }} />
      </Modal>
      <Modal open={modalMensal} onClose={() => setModalMensal(false)} title="Novo Planejamento Mensal">
        <FormPlanejamentoMensal clientes={clientes} onSalvar={salvarMensal} onFechar={() => setModalMensal(false)} />
      </Modal>
    </div>
  );
}

function FormPlanejamentoSemestral({ inicial, clientes, onSalvar, onFechar }) {
  const [form, setForm] = useState(inicial ? { ...inicial, temas: Array.isArray(inicial.temas) ? inicial.temas.join(', ') : '' } : { cliente_id: '', semestre: 1, ano: new Date().getFullYear(), objetivos: '', temas: '', quantidade_posts_semana: 5, plataformas: [] });
  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const togglePlat = (pl) => setForm(p => ({ ...p, plataformas: p.plataformas.includes(pl) ? p.plataformas.filter(x => x !== pl) : [...p.plataformas, pl] }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar({ ...form, temas: form.temas.split(',').map(t => t.trim()).filter(Boolean) }); }} className="space-y-4">
      <div><label className="form-label">Cliente *</label>
        <select className="form-input" value={form.cliente_id} onChange={e => s('cliente_id', e.target.value)} required>
          <option value="">Selecione...</option>{clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><label className="form-label">Semestre</label>
          <select className="form-input" value={form.semestre} onChange={e => s('semestre', parseInt(e.target.value))}>
            <option value={1}>1º Semestre</option><option value={2}>2º Semestre</option>
          </select>
        </div>
        <div><label className="form-label">Ano</label><input type="number" className="form-input" value={form.ano} onChange={e => s('ano', parseInt(e.target.value))} /></div>
        <div><label className="form-label">Posts/semana</label><input type="number" className="form-input" value={form.quantidade_posts_semana} onChange={e => s('quantidade_posts_semana', parseInt(e.target.value))} /></div>
      </div>
      <div><label className="form-label">Objetivo *</label><textarea className="form-input" rows={2} value={form.objetivos} onChange={e => s('objetivos', e.target.value)} required /></div>
      <div><label className="form-label">Temas (separados por vírgula)</label><input className="form-input" value={form.temas} onChange={e => s('temas', e.target.value)} /></div>
      <div><label className="form-label">Plataformas</label>
        <div className="flex gap-2 flex-wrap mt-1">
          {['instagram','facebook','tiktok','linkedin','youtube'].map(pl => (
            <button type="button" key={pl} onClick={() => togglePlat(pl)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${form.plataformas.includes(pl) ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600'}`}>{pl}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-2"><button type="submit" className="btn-primary flex-1">Salvar</button><button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button></div>
    </form>
  );
}

function FormPlanejamentoMensal({ clientes, onSalvar, onFechar }) {
  const [form, setForm] = useState({ cliente_id: '', mes: new Date().getMonth() + 1, ano: new Date().getFullYear(), observacoes: '', grade: [] });
  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(form); }} className="space-y-4">
      <div><label className="form-label">Cliente *</label>
        <select className="form-input" value={form.cliente_id} onChange={e => s('cliente_id', e.target.value)} required>
          <option value="">Selecione...</option>{clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="form-label">Mês</label>
          <select className="form-input" value={form.mes} onChange={e => s('mes', parseInt(e.target.value))}>
            {['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'].map((m, i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
        </div>
        <div><label className="form-label">Ano</label><input type="number" className="form-input" value={form.ano} onChange={e => s('ano', parseInt(e.target.value))} /></div>
      </div>
      <div><label className="form-label">Observações</label><textarea className="form-input" rows={2} value={form.observacoes} onChange={e => s('observacoes', e.target.value)} /></div>
      <div className="flex gap-2"><button type="submit" className="btn-primary flex-1">Criar</button><button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button></div>
    </form>
  );
}

// ─── ABA CAPTAÇÕES ────────────────────────────────────────────────────────────
function TabCaptacoes() {
  const [captacoes, setCaptacoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);

  const carregar = useCallback(async () => {
    try { const { data } = await api.get('/midias/captacoes'); setCaptacoes(data || []); }
    catch { toast.error('Erro ao carregar captações.'); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/clientes/lista').then(r => setClientes(r.data || [])).catch(() => {});
    api.get('/usuarios/equipe').then(r => setUsuarios(r.data || [])).catch(() => {});
  }, []);

  const salvar = async (dados) => {
    try {
      if (editando?.id) { await api.patch(`/midias/captacoes/${editando.id}`, dados); toast.success('Captação atualizada!'); }
      else { await api.post('/midias/captacoes', dados); toast.success('Captação agendada!'); }
      setModal(false); setEditando(null); carregar();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro.'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta captação?')) return;
    try { await api.delete(`/midias/captacoes/${id}`); toast.success('Excluída.'); carregar(); }
    catch { toast.error('Erro ao excluir.'); }
  };

  const atualizarStatus = async (id, status) => {
    try { await api.patch(`/midias/captacoes/${id}`, { status }); carregar(); }
    catch { toast.error('Erro.'); }
  };

  return (
    <div>
      <div className="flex justify-end mb-5"><button onClick={() => setModal(true)} className="btn-primary">+ Nova Captação</button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {captacoes.map(cap => (
          <div key={cap.id} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-semibold text-gray-900">{cap.cliente_nome}</p>
                <p className="text-xs text-gray-500 mt-0.5">{cap.local}</p>
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${cap.status === 'realizado' ? 'bg-green-50 text-green-700' : cap.status === 'cancelado' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'}`}>{cap.status}</span>
            </div>
            <p className="text-xs text-brand-700 font-semibold mb-1 flex items-center gap-1">
              <IcCalendar size={11} /> {cap.data_agendada ? format(new Date(cap.data_agendada), 'dd/MM/yyyy', { locale: ptBR }) : '—'}
            </p>
            {cap.filmmaker_nome && <p className="text-xs text-gray-500 mb-2 flex items-center gap-1"><IcFilm size={11} /> {cap.filmmaker_nome}</p>}
            <div className="flex gap-1 mb-2 flex-wrap">
              {(cap.tipo_conteudo || []).map(t => <span key={t} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{t}</span>)}
              {cap.quantidade_estimada && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{cap.quantidade_estimada} arq.</span>}
            </div>
            {cap.briefing && <p className="text-xs text-gray-500 italic line-clamp-2">{cap.briefing}</p>}
            <div className="flex gap-2 mt-3 flex-wrap">
              {cap.status === 'agendado' && <button onClick={() => atualizarStatus(cap.id, 'realizado')} className="text-xs text-green-600 hover:underline font-medium flex items-center gap-1"><IcCheckCircle size={11} /> Realizada</button>}
              {cap.status === 'agendado' && <button onClick={() => atualizarStatus(cap.id, 'cancelado')} className="text-xs text-red-500 hover:underline">Cancelar</button>}
              <button onClick={() => { setEditando(cap); setModal(true); }} className="text-xs text-brand-600 hover:underline font-medium">Editar</button>
              <button onClick={() => excluir(cap.id)} className="text-xs text-red-500 hover:underline">Excluir</button>
            </div>
          </div>
        ))}
        {!captacoes.length && <div className="col-span-3 text-center py-12 text-gray-400">Nenhuma captação agendada.</div>}
      </div>
      <Modal open={modal} onClose={() => { setModal(false); setEditando(null); }} title={editando ? 'Editar Captação' : 'Nova Captação'}>
        <FormCaptacao inicial={editando} clientes={clientes} usuarios={usuarios} onSalvar={salvar} onFechar={() => { setModal(false); setEditando(null); }} />
      </Modal>
    </div>
  );
}

function FormCaptacao({ inicial, clientes, usuarios, onSalvar, onFechar }) {
  const [form, setForm] = useState(inicial || { cliente_id: '', data_agendada: '', local: '', tipo_conteudo: [], quantidade_estimada: 20, briefing: '', responsavel_filmmaker: '' });
  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleTipo = (t) => setForm(p => ({ ...p, tipo_conteudo: p.tipo_conteudo.includes(t) ? p.tipo_conteudo.filter(x => x !== t) : [...p.tipo_conteudo, t] }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(form); }} className="space-y-4">
      <div><label className="form-label">Cliente *</label>
        <select className="form-input" value={form.cliente_id} onChange={e => s('cliente_id', e.target.value)} required>
          <option value="">Selecione...</option>{clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="form-label">Data *</label><input type="date" className="form-input" value={form.data_agendada?.slice(0,10) || ''} onChange={e => s('data_agendada', e.target.value)} required /></div>
        <div><label className="form-label">Filmmaker</label>
          <select className="form-input" value={form.responsavel_filmmaker || ''} onChange={e => s('responsavel_filmmaker', e.target.value)}>
            <option value="">Selecione...</option>{usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>
      </div>
      <div><label className="form-label">Local *</label><input className="form-input" value={form.local} onChange={e => s('local', e.target.value)} required /></div>
      <div><label className="form-label">Tipo de conteúdo</label>
        <div className="flex gap-2 flex-wrap mt-1">
          {['fotos','videos','reels','stories'].map(t => (
            <button type="button" key={t} onClick={() => toggleTipo(t)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${(form.tipo_conteudo || []).includes(t) ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600'}`}>{t}</button>
          ))}
        </div>
      </div>
      <div><label className="form-label">Qtd. estimada</label><input type="number" className="form-input" value={form.quantidade_estimada} onChange={e => s('quantidade_estimada', parseInt(e.target.value))} /></div>
      <div><label className="form-label">Briefing</label><textarea className="form-input" rows={3} value={form.briefing || ''} onChange={e => s('briefing', e.target.value)} /></div>
      <div className="flex gap-2"><button type="submit" className="btn-primary flex-1">Agendar</button><button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button></div>
    </form>
  );
}

// ─── ABA DEMANDAS EXTRAS ─────────────────────────────────────────────────────
function TabDemandasExtras() {
  const usuario = useAppStore(s => s.usuario);
  const [demandas, setDemandas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [modal, setModal] = useState(false);
  const [editandoDemanda, setEditandoDemanda] = useState(null);
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const carregar = useCallback(async () => {
    try {
      const params = {};
      if (filtroCliente) params.cliente_id = filtroCliente;
      if (filtroStatus) params.status = filtroStatus;
      const { data } = await api.get('/midias/demandas-extras', { params });
      setDemandas(data || []);
    } catch { toast.error('Erro ao carregar demandas.'); }
  }, [filtroCliente, filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { api.get('/clientes/lista').then(r => setClientes(r.data || [])).catch(() => {}); }, []);

  const salvar = async (dados) => {
    try {
      if (editandoDemanda?.id) { await api.patch(`/midias/demandas-extras/${editandoDemanda.id}`, dados); toast.success('Demanda atualizada!'); }
      else { await api.post('/midias/demandas-extras', dados); toast.success('Demanda criada!'); }
      setModal(false); setEditandoDemanda(null); carregar();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro.'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta demanda?')) return;
    try { await api.delete(`/midias/demandas-extras/${id}`); toast.success('Excluída.'); carregar(); }
    catch { toast.error('Erro ao excluir.'); }
  };

  const PRIOR_COR = { alta: 'bg-red-50 text-red-700', media: 'bg-yellow-50 text-yellow-700', baixa: 'bg-gray-100 text-gray-600' };
  const STATUS_COR = { pendente: 'bg-yellow-50 text-yellow-700', em_andamento: 'bg-blue-50 text-blue-700', concluido: 'bg-green-50 text-green-700' };

  return (
    <div>
      <div className="flex gap-3 mb-5 flex-wrap items-center">
        <select className="form-input w-auto" value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
          <option value="">Todos os clientes</option>
          {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
        </select>
        <select className="form-input w-auto" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="pendente">Pendente</option><option value="em_andamento">Em andamento</option><option value="concluido">Concluído</option>
        </select>
        <button onClick={() => setModal(true)} className="btn-primary ml-auto">+ Nova Demanda</button>
      </div>

      <div className="space-y-3">
        {demandas.map(d => (
          <div key={d.id} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <p className="text-sm font-semibold text-gray-900">{d.titulo}</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${PRIOR_COR[d.prioridade]}`}>{d.prioridade}</span>
              </div>
              <p className="text-xs text-brand-600 font-medium mb-1">{d.cliente_nome}</p>
              {d.solicitante && <p className="text-xs text-gray-400 mb-1">Solicitado por: {d.solicitante}</p>}
              <p className="text-sm text-gray-600">{d.descricao}</p>
              <p className="text-xs text-gray-400 mt-1">{format(new Date(d.criado_em), "dd/MM/yyyy 'às' HH:mm")}</p>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${STATUS_COR[d.status]}`}>{d.status.replace('_', ' ')}</span>
              <select className="form-input text-xs w-36" value={d.status}
                onChange={async e => { await api.patch(`/midias/demandas-extras/${d.id}`, { status: e.target.value }); carregar(); toast.success('Status atualizado!'); }}>
                <option value="pendente">Pendente</option>
                <option value="em_andamento">Em andamento</option>
                <option value="concluido">Concluído</option>
              </select>
              <div className="flex gap-2">
                <button onClick={() => { setEditandoDemanda(d); setModal(true); }} className="text-xs text-brand-600 hover:underline font-medium">Editar</button>
                <button onClick={() => excluir(d.id)} className="text-xs text-red-500 hover:underline">Excluir</button>
              </div>
            </div>
          </div>
        ))}
        {!demandas.length && <p className="text-center text-gray-400 py-12">Nenhuma demanda extra encontrada.</p>}
      </div>

      <Modal open={modal} onClose={() => { setModal(false); setEditandoDemanda(null); }} title={editandoDemanda ? 'Editar Demanda' : 'Nova Demanda Extra'}>
        <FormDemandaExtra inicial={editandoDemanda} clientes={clientes} onSalvar={salvar} onFechar={() => { setModal(false); setEditandoDemanda(null); }} />
      </Modal>
    </div>
  );
}

function FormDemandaExtra({ inicial, clientes, onSalvar, onFechar }) {
  const usuario = useAppStore(s => s.usuario);
  const [form, setForm] = useState(inicial || { cliente_id: '', titulo: '', descricao: '', prioridade: 'media', solicitante: usuario?.nome || 'Equipe Legacy' });
  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(form); }} className="space-y-4">
      <div><label className="form-label">Cliente *</label>
        <select className="form-input" value={form.cliente_id} onChange={e => s('cliente_id', e.target.value)} required>
          <option value="">Selecione...</option>{clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
        </select>
      </div>
      <div><label className="form-label">Título *</label><input className="form-input" value={form.titulo} onChange={e => s('titulo', e.target.value)} required /></div>
      <div><label className="form-label">Descrição *</label><textarea className="form-input" rows={3} value={form.descricao} onChange={e => s('descricao', e.target.value)} required /></div>
      <div><label className="form-label">Prioridade</label>
        <select className="form-input" value={form.prioridade} onChange={e => s('prioridade', e.target.value)}>
          <option value="alta">Alta</option><option value="media">Média</option><option value="baixa">Baixa</option>
        </select>
      </div>
      <div className="flex gap-2"><button type="submit" className="btn-primary flex-1">Criar</button><button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button></div>
    </form>
  );
}
